import { useCallback, useEffect, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useEnabledFeatures } from '../../common/hooks/enabled-features/use-enabled-features';
import {
    CallSignOption,
    EditUnitFormType,
    EditUnitOptions,
    EditUnitStatics,
    EditUnitView,
    UserOption,
} from '@mark43/cad-api';

import { formatHoursMinutes } from '../../common/helpers/utils';
import { useApplicationType } from '../../common/hooks/use-application-type';
import { getFirstResponderPersonnelFormValue, isValidSelectedPersonnel } from '../helpers';
import { useUnitAssignment } from '../../unit-assignment/unit-assignment-context';

const defaultEditUnitOptions: EditUnitOptions = {
    callSignOptions: [],
    dispatchAreaOptions: [],
    equipmentOptions: [],
    unitTypeOptions: [],
    vehicleTagOptions: [],
    beatSubdivisionOptions: [],
    stationOptions: [],
    radioOptions: [],
};

export function useEditUnitFormState({
    form,
    existingUnitData,
    editUnitOptions,
    updateCurrentCallSign,
    isLogOnUnitForm,
    defaultPersonnel,
}: {
    form: UseFormReturn<EditUnitFormType>;
    existingUnitData?: EditUnitView;
    editUnitOptions?: EditUnitOptions;
    updateCurrentCallSign?: (callSign: CallSignOption) => void;
    isLogOnUnitForm?: boolean;
    defaultPersonnel?: UserOption;
}) {
    const [userChangedStaffCount, setUserChangedStaffCount] = useState(false);
    const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
    const [formFieldOptions, setFormFieldOptions] = useState<EditUnitOptions>(
        defaultEditUnitOptions
    );

    const { isFirstResponder } = useApplicationType();
    const unitAssignment = useUnitAssignment();

    const { multiAgencySubdivisions } = useEnabledFeatures();
    const defaultBeatSubdivisionLabel = multiAgencySubdivisions ? 'Primary Subdivision' : 'Beat';

    const [statics, setStatics] = useState<EditUnitStatics>({
        alreadyInUnitUserIds: [],
        beatSubdivisionLabel: defaultBeatSubdivisionLabel,
        isMemberRequired: false,
    });

    const getUpdatedStaffCount = useCallback(
        ({
            currentStaffCount,
            defaultStaffCount,
            staffCount,
        }: {
            currentStaffCount: number | null;
            defaultStaffCount?: number;
            staffCount?: number;
        }): number | null | undefined => {
            if (userChangedStaffCount) {
                return currentStaffCount;
            }

            if (isLogOnUnitForm || (isFirstResponder && !unitAssignment?.unitId)) {
                return defaultStaffCount;
            }

            return staffCount;
        },
        [isLogOnUnitForm, userChangedStaffCount, unitAssignment, isFirstResponder]
    );

    const synchronizeFormState = useCallback(
        (existingUnitData: EditUnitView) => {
            setFormFieldOptions(existingUnitData.options);
            setStatics({
                ...existingUnitData.statics,
                beatSubdivisionLabel:
                    existingUnitData.statics.beatSubdivisionLabel || defaultBeatSubdivisionLabel,
            });
            const existingPersonnel = form.getValues().personnel.filter(isValidSelectedPersonnel);
            const currentStaffCount = form.getValues('staffCount');

            const cadPersonnel =
                existingPersonnel.length > 0 ? existingPersonnel : existingUnitData.form.personnel;

            const firstResponderPersonnel = getFirstResponderPersonnelFormValue(
                existingPersonnel,
                existingUnitData,
                defaultPersonnel
            );

            form.reset(
                {
                    ...existingUnitData.form,
                    personnel: isFirstResponder ? firstResponderPersonnel : cadPersonnel,
                    shiftStart: existingUnitData.form.shiftStart
                        ? formatHoursMinutes(existingUnitData.form.shiftStart)
                        : '',
                    shiftEnd: existingUnitData.form.shiftEnd
                        ? formatHoursMinutes(existingUnitData.form.shiftEnd)
                        : '',
                    staffCount: getUpdatedStaffCount({
                        currentStaffCount,
                        defaultStaffCount: existingUnitData.statics?.defaultStaffCount,
                        staffCount: existingUnitData.form?.staffCount,
                    }),
                },
                {
                    keepDefaultValues: true,
                }
            );
        },
        [
            defaultBeatSubdivisionLabel,
            defaultPersonnel,
            form,
            getUpdatedStaffCount,
            isFirstResponder,
        ]
    );

    // Synchronize form state when editUnitView changes
    useEffect(() => {
        if (existingUnitData) {
            synchronizeFormState(existingUnitData);
            setHasLoadedOnce(true);

            if (isFirstResponder && updateCurrentCallSign) {
                updateCurrentCallSign(existingUnitData.form.callSign);
            }
        } else if (editUnitOptions) {
            setFormFieldOptions(editUnitOptions);
            setHasLoadedOnce(true);
        }
    }, [
        existingUnitData,
        editUnitOptions,
        isFirstResponder,
        synchronizeFormState,
        updateCurrentCallSign,
    ]);

    return { hasLoadedOnce, formFieldOptions, statics, setUserChangedStaffCount };
}
